import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FirstPageRounded from "@mui/icons-material/FirstPageRounded";
import KeyboardArrowLeftRounded from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import LastPageRounded from "@mui/icons-material/LastPageRounded";

const paginationButtons = [
  { icon: FirstPageRounded, id: "first" },
  { icon: KeyboardArrowLeftRounded, id: "previous" },
  { icon: KeyboardArrowRightRounded, id: "next" },
  { icon: LastPageRounded, id: "last" },
];

function getDefaultFooter(rows) {
  const total = rows.length;
  const plural = total === 1 ? "registro" : "registros";

  return {
    rangeLabel: total > 0 ? `1-${total} de ${total}` : "0-0 de 0",
    totalLabel: `Total: ${total} ${plural}`,
  };
}

function renderCell(column, row) {
  if (column.renderCell) {
    return column.renderCell(row);
  }

  return row[column.id];
}

function SettingsDataTableSkeletonRows({ columns, rowsCount }) {
  return Array.from({ length: rowsCount }, (_, rowIndex) => (
    <Box component="tr" key={`settings-table-skeleton-${rowIndex}`}>
      {columns.map((column, columnIndex) => (
        <Box
          component="td"
          key={column.id}
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.palette.border.muted}`,
            borderLeft: columnIndex === 0 ? 0 : `1px solid ${theme.palette.border.muted}`,
            px: 2,
            py: 1.6,
          })}
        >
          <Skeleton
            animation="wave"
            height={18}
            sx={{ maxWidth: columnIndex === columns.length - 1 ? 360 : 160 }}
            width={columnIndex === columns.length - 1 ? "72%" : "64%"}
          />
        </Box>
      ))}
    </Box>
  ));
}

export default function SettingsDataTable({
  actions,
  columns,
  emptyMessage = "Nenhum registro encontrado.",
  footer,
  getRowKey = (row) => row.id,
  loading = false,
  minWidth = 720,
  rows,
  skeletonRowsCount = 5,
  title,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const footerConfig = footer === undefined ? (loading ? false : getDefaultFooter(safeRows)) : footer;
  const showFooter = footerConfig !== false;
  const scrollContainerRef = React.useRef(null);
  const [mobileScrollbar, setMobileScrollbar] = React.useState({
    thumbLeft: 0,
    thumbWidth: 100,
    visible: false,
  });

  const updateMobileScrollbar = React.useCallback(() => {
    const node = scrollContainerRef.current;

    if (!node) {
      return;
    }

    const maxScroll = Math.max(0, node.scrollWidth - node.clientWidth);
    const visible = maxScroll > 2;
    const thumbWidth = visible
      ? Math.max(18, Math.min(100, (node.clientWidth / node.scrollWidth) * 100))
      : 100;
    const thumbLeft = visible
      ? Math.min(100 - thumbWidth, Math.max(0, (node.scrollLeft / maxScroll) * (100 - thumbWidth)))
      : 0;

    setMobileScrollbar((current) => {
      const sameVisibility = current.visible === visible;
      const sameWidth = Math.abs(current.thumbWidth - thumbWidth) < 0.1;
      const sameLeft = Math.abs(current.thumbLeft - thumbLeft) < 0.1;

      if (sameVisibility && sameWidth && sameLeft) {
        return current;
      }

      return {
        thumbLeft,
        thumbWidth,
        visible,
      };
    });
  }, []);

  React.useEffect(() => {
    updateMobileScrollbar();
  }, [columns.length, loading, minWidth, safeRows.length, updateMobileScrollbar]);

  React.useEffect(() => {
    const node = scrollContainerRef.current;

    if (!node) {
      return undefined;
    }

    node.addEventListener("scroll", updateMobileScrollbar, { passive: true });
    window.addEventListener("resize", updateMobileScrollbar);

    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateMobileScrollbar);
    resizeObserver?.observe(node);
    updateMobileScrollbar();

    return () => {
      node.removeEventListener("scroll", updateMobileScrollbar);
      window.removeEventListener("resize", updateMobileScrollbar);
      resizeObserver?.disconnect();
    };
  }, [updateMobileScrollbar]);

  const handleMobileScrollbarPointerDown = React.useCallback((event) => {
    const node = scrollContainerRef.current;

    if (!node) {
      return;
    }

    const trackRect = event.currentTarget.getBoundingClientRect();
    const position = Math.min(1, Math.max(0, (event.clientX - trackRect.left) / trackRect.width));

    node.scrollTo({
      behavior: "smooth",
      left: position * Math.max(0, node.scrollWidth - node.clientWidth),
    });
  }, []);

  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.border.default}`,
        borderRadius: "12px",
        boxShadow: theme.shadows[1],
        overflow: "hidden",
      })}
    >
      {title || actions ? (
        <Box
          sx={(theme) => ({
            alignItems: { xs: "stretch", sm: "center" },
            borderBottom: `1px solid ${theme.palette.border.default}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "space-between",
            minHeight: 72,
            px: 2,
            py: 2,
          })}
        >
          {title ? (
            <Typography
              component="h3"
              sx={{
                color: "text.primary",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: 0,
                lineHeight: "22px",
              }}
            >
              {title}
            </Typography>
          ) : null}
          {actions ? (
            <Stack direction="row" spacing={1.5} sx={{ justifyContent: "flex-end" }}>
              {actions}
            </Stack>
          ) : null}
        </Box>
      ) : null}

      <Box
        ref={scrollContainerRef}
        sx={(theme) => {
          const scrollbarThumb =
            theme.palette.mode === "dark"
              ? theme.palette.custom.gray[500]
              : theme.palette.custom.gray[400];
          const scrollbarTrack =
            theme.palette.mode === "dark"
              ? theme.palette.custom.gray[800]
              : theme.palette.custom.gray[100];

          return {
            overflowX: "auto",
            overflowY: "hidden",
            pb: 0,
            scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
            scrollbarWidth: { xs: "none", sm: "thin" },
            "&::-webkit-scrollbar": {
              height: { xs: 0, sm: 8 },
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scrollbarThumb,
              border: `3px solid ${scrollbarTrack}`,
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: scrollbarTrack,
              borderRadius: 999,
            },
          };
        }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth,
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {columns.map((column, index) => (
                <Box
                  component="th"
                  key={column.id}
                  sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.border.default}`,
                    borderLeft: index === 0 ? 0 : `1px solid ${theme.palette.border.default}`,
                    color: "text.secondary",
                    fontSize: 12,
                    fontWeight: 700,
                    height: 48,
                    letterSpacing: 0,
                    lineHeight: "16px",
                    px: 2,
                    textAlign: column.align || "left",
                    verticalAlign: "middle",
                    width: column.width,
                  })}
                >
                  {column.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {loading ? (
              <SettingsDataTableSkeletonRows columns={columns} rowsCount={skeletonRowsCount} />
            ) : safeRows.length ? (
              safeRows.map((row) => (
                <Box component="tr" key={getRowKey(row)}>
                  {columns.map((column, index) => (
                    <Box
                      component="td"
                      key={column.id}
                      sx={(theme) => ({
                        borderBottom: `1px solid ${theme.palette.border.muted}`,
                        borderLeft: index === 0 ? 0 : `1px solid ${theme.palette.border.muted}`,
                        px: 2,
                        py: 1.5,
                        textAlign: column.align || "left",
                        verticalAlign: "middle",
                      })}
                    >
                      {renderCell(column, row)}
                    </Box>
                  ))}
                </Box>
              ))
            ) : (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={columns.length}
                  sx={{
                    color: "text.secondary",
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: "20px",
                    px: 2,
                    py: 4,
                    textAlign: "center",
                  }}
                >
                  {emptyMessage}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {!loading && mobileScrollbar.visible ? (
        <Box
          aria-hidden="true"
          onPointerDown={handleMobileScrollbarPointerDown}
          sx={(theme) => ({
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.border.default}`,
            cursor: "pointer",
            display: { xs: "block", sm: "none" },
            px: 2,
            py: 1,
          })}
        >
          <Box
            sx={(theme) => ({
              bgcolor:
                theme.palette.mode === "dark"
                  ? theme.palette.custom.gray[800]
                  : theme.palette.custom.gray[200],
              borderRadius: 999,
              height: 8,
              overflow: "hidden",
              position: "relative",
            })}
          >
            <Box
              sx={(theme) => ({
                bgcolor:
                  theme.palette.mode === "dark"
                    ? theme.palette.custom.gray[400]
                    : theme.palette.custom.gray[500],
                borderRadius: 999,
                bottom: 0,
                left: `${mobileScrollbar.thumbLeft}%`,
                position: "absolute",
                top: 0,
                transition: "left 120ms ease, width 120ms ease",
                width: `${mobileScrollbar.thumbWidth}%`,
              })}
            />
          </Box>
        </Box>
      ) : null}

      {showFooter ? (
        <Box
          sx={(theme) => ({
            alignItems: "center",
            borderTop: `1px solid ${theme.palette.border.default}`,
            color: "text.secondary",
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 3 },
            justifyContent: "flex-end",
            minHeight: 48,
            px: 2,
            py: 1,
          })}
        >
          {footerConfig?.totalLabel ? (
            <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 500 }}>
              {footerConfig.totalLabel}
            </Typography>
          ) : null}
          {footerConfig?.rangeLabel ? (
            <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 500 }}>
              {footerConfig.rangeLabel}
            </Typography>
          ) : null}
          {footerConfig?.showPagination !== false ? (
            <Stack direction="row" spacing={0.25}>
              {paginationButtons.map(({ icon: Icon, id }) => (
                <IconButton
                  aria-hidden="true"
                  disabled
                  key={id}
                  size="small"
                  tabIndex={-1}
                  sx={{
                    color: "text.secondary",
                    height: 28,
                    width: 28,
                    "&.Mui-disabled": {
                      color: "text.disabled",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
